import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageTransition from "../components/PageTransition";
import PageHero from "../components/PageHero";
import ImagePlaceholder from "../components/ImagePlaceholder";
import { services } from "../data/services";
import { useTranslation } from "../i18n/LanguageContext";

export default function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const service = services.find((s) => s.slug === slug);
  const t = useTranslation();

  if (!service) return <Navigate to="/services" replace />;

  const others = services.filter((s) => s.slug !== slug);

  return (
    <PageTransition>
      <PageHero title={service.title} description={service.subtitle} />

      <section className="py-24 md:py-32">
        <div className="max-w-content mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-20">
            <div className="lg:col-span-2">
              <ImagePlaceholder label={service.imageLabel} src={service.imageSrc} aspectRatio="16/9" className="mb-14" />
              <p className="text-secondary text-[17px] leading-[1.8] mb-14">{service.description}</p>

              <h3 className="text-[12px] uppercase tracking-[0.1em] font-semibold text-primary mb-8">{t.svc_detail_highlights}</h3>
              <ul className="space-y-4 mb-16">
                {service.highlights.map((h) => (
                  <li key={h} className="flex items-baseline gap-4 pb-4 border-b border-border">
                    <span className="text-accent text-sm">—</span>
                    <span className="text-secondary text-[15px]">{h}</span>
                  </li>
                ))}
              </ul>

              {service.process && (
                <>
                  <h3 className="text-[12px] uppercase tracking-[0.1em] font-semibold text-primary mb-10">{t.svc_detail_process}</h3>
                  <div className="relative">
                    <div className="hidden md:block absolute top-5 left-0 right-0 h-px bg-border" />
                    <div className="grid md:grid-cols-5 gap-8">
                      {service.process.map((p) => (
                        <div key={p.step} className="relative">
                          <div className="relative z-10 w-10 h-10 border-2 border-accent bg-white flex items-center justify-center mb-4">
                            <span className="text-[13px] font-semibold text-accent">{p.step}</span>
                          </div>
                          <h4 className="font-semibold text-primary text-[15px] mb-2">{p.title}</h4>
                          <p className="text-secondary text-[14px] leading-relaxed">{p.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-28 space-y-10">
                <div className="border border-border p-8">
                  <h4 className="font-serif text-xl mb-3">{t.svc_detail_cta_title}</h4>
                  <p className="text-secondary text-[15px] mb-6">{t.svc_detail_cta_text}</p>
                  <Link to="/contact" className="flex items-center justify-center gap-2 w-full text-[13px] uppercase tracking-[0.08em] font-semibold text-white bg-accent py-4 hover:bg-accent-dark transition-colors duration-300">
                    {t.svc_detail_cta_button} <ArrowRight size={14} />
                  </Link>
                </div>

                <div>
                  <h4 className="text-[12px] uppercase tracking-[0.1em] font-semibold text-secondary mb-5">{t.svc_detail_others}</h4>
                  <nav className="space-y-1">
                    {others.map((o) => (
                      <Link key={o.slug} to={`/services/${o.slug}`} className="flex items-center justify-between py-4 border-b border-border text-[15px] text-primary hover:text-accent transition-colors duration-300">
                        {o.title}
                        <ArrowRight size={14} className="text-secondary" />
                      </Link>
                    ))}
                  </nav>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
